import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
});

// SINGLE-SESSION ENFORCEMENT — if the backend returns 401 with detail
// "session_revoked", the current tab/device has been kicked out by a newer
// login on another device. Notify the user, clear local auth state, and
// bounce them to /login. We use a module-level flag to make sure we only
// fire the toast + redirect once even if multiple parallel requests fail.
let sessionRevokedHandled = false;
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        if (status === 401 && detail === "session_revoked" && !sessionRevokedHandled) {
            sessionRevokedHandled = true;
            try {
                // Dynamic imports keep this file dependency-light at module load.
                const { toast } = await import("sonner");
                toast.error("You've been signed in from another device. Please sign in again.", { duration: 8000 });
            } catch { /* ignore — toast is best-effort */ }
            try {
                // Best-effort cookie cleanup; backend already invalidated them.
                document.cookie = "access_token=; Max-Age=0; path=/";
                document.cookie = "refresh_token=; Max-Age=0; path=/";
            } catch { /* ignore */ }
            // Allow the toast to flash, then hard-redirect so all in-memory
            // React state is wiped (cleaner than navigate() which would keep
            // stale auth context alive in other tabs).
            setTimeout(() => {
                try { window.location.assign("/login?reason=session_revoked"); }
                catch { window.location.href = "/login"; }
            }, 700);
        }
        return Promise.reject(error);
    }
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
